import { Component, output, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { Clasificacion } from 'src/app/domain/model/clasificacion/clasificacion';
import { ClasificacionRepository } from 'src/app/domain/service/clasificacion/clasificacion-repository';
import { DEFAULT_ROWS_PER_PAGE, PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';
import { sanitizeNumbersOnly, sanitizeText } from 'src/app/common/input-utils';

export interface ClasificacionSeleccionada {
  cdclasificacion: number;
  dsclasificacion: string;
}

@Component({
  selector: 'app-busqueda-clasificacion-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ProgressSpinnerModule,
    TooltipModule
  ],
  template: `
    <p-dialog
      header="Búsqueda de Clasificación"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '44rem' }"
      [closable]="true"
      (onHide)="onCancelar()">

      <div class="border rounded p-4 mb-4 bg-gray-50">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm text-gray-600 mb-1">Código</label>
            <input pInputText
              [(ngModel)]="codigo"
              class="w-full"
              maxlength="5"
              placeholder="Ej: 3"
              (input)="codigo = sanitizeNumbersOnly($any($event.target).value).slice(0, 5)"
              (keyup.enter)="buscar()" />
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1">Descripción</label>
            <input pInputText
              [(ngModel)]="descripcion"
              class="w-full"
              maxlength="50"
              placeholder="Ej: ASESORÍA"
              (input)="descripcion = sanitizeText($any($event.target).value).slice(0, 50)"
              (keyup.enter)="buscar()" />
          </div>
        </div>
        <div class="flex flex-wrap gap-2 mt-4">
          <button pButton icon="pi pi-search" label="Buscar"
            class="p-button-sm"
            (click)="buscar()"
            [disabled]="loading()">
          </button>
          <button pButton icon="pi pi-trash" label="Limpiar"
            class="p-button-outlined p-button-sm p-button-secondary"
            (click)="limpiarFiltros()">
          </button>
        </div>
      </div>

      @if (mensaje()) {
        <div class="p-3 mb-3 rounded bg-yellow-100 text-yellow-800 text-sm">
          <i class="pi pi-exclamation-triangle mr-2"></i>{{ mensaje() }}
        </div>
      }

      @if (loading()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner strokeWidth="4" [style]="{ width: '50px', height: '50px' }"></p-progressSpinner>
        </div>
      }

      @if (!loading() && items().length > 0) {
        <p-table
          [value]="items()"
          [scrollable]="true"
          [responsiveLayout]="'scroll'"
          scrollHeight="300px"
          selectionMode="single"
          [(selection)]="seleccionado"
          styleClass="p-datatable-sm p-datatable-gridlines p-datatable-hoverable-rows"
          dataKey="cdclasificacion">

          <ng-template pTemplate="header">
            <tr>
              <th style="width: 30%">Código</th>
              <th>Clasificación</th>
              <th style="width: 100px" class="text-center">Seleccionar</th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-item>
            <tr [pSelectableRow]="item" class="cursor-pointer">
              <td>{{ item.cdclasificacion }}</td>
              <td>{{ item.dsclasificacion }}</td>
              <td class="text-center">
                <button pButton
                  label="Seleccionar"
                  class="p-button-sm p-button-text"
                  (click)="seleccionar(item)">
                </button>
              </td>
            </tr>
          </ng-template>
        </p-table>

        <div class="flex justify-between items-center p-3 border-t mt-2">
          <span class="text-sm text-gray-500">
            @if (totalRegistros() > 0) {
              Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de
              {{ totalRegistros() }}
            }
          </span>
          <div class="flex gap-2 items-center">
            <label class="text-sm text-gray-600">Registros:</label>
            <select class="border rounded px-2 py-1 text-sm" [(ngModel)]="selectedPageSize" (change)="onPageSizeChange()">
              @for (size of pageSizeOptions; track size) {
                <option [value]="size">{{ size }}</option>
              }
            </select>
            <button pButton icon="pi pi-angle-left"
              class="p-button-sm p-button-secondary min-w-[44px] min-h-[44px]"
              pTooltip="Página anterior"
              [disabled]="currentPage() === 0"
              (click)="cambiarPagina(currentPage() - 1)">
            </button>
            <span class="text-sm flex items-center px-2">
              Pág. {{ currentPage() + 1 }} / {{ totalPages() }}
            </span>
            <button pButton icon="pi pi-angle-right"
              class="p-button-sm p-button-secondary min-w-[44px] min-h-[44px]"
              pTooltip="Página siguiente"
              [disabled]="currentPage() >= totalPages() - 1"
              (click)="cambiarPagina(currentPage() + 1)">
            </button>
          </div>
        </div>
      }

      <ng-template pTemplate="footer">
        <button pButton label="Cancelar" icon="pi pi-times"
          class="p-button-outlined p-button-secondary p-button-sm"
          (click)="onCancelar()">
        </button>
      </ng-template>
    </p-dialog>
  `
})
export class BusquedaClasificacionDialogComponent {
  private readonly clasificacionRepo = inject(ClasificacionRepository);

  visible = true;

  onSelect = output<ClasificacionSeleccionada>();
  onClose = output<void>();

  loading = signal(false);
  items = signal<Clasificacion[]>([]);
  totalRegistros = signal(0);
  currentPage = signal(0);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  mensaje = signal<string | null>(null);
  seleccionado: Clasificacion | null = null;

  codigo = '';
  descripcion = '';

  readonly sanitizeNumbersOnly = sanitizeNumbersOnly;
  readonly sanitizeText = sanitizeText;

  totalPages = () => Math.max(1, Math.ceil(this.totalRegistros() / this.pageSize()));

  registroInicio = () => {
    if (this.totalRegistros() === 0) return 0;
    return this.currentPage() * this.pageSize() + 1;
  };

  registroFin = () =>
    Math.min((this.currentPage() + 1) * this.pageSize(), this.totalRegistros());

  async buscar(page = 0): Promise<void> {
    this.loading.set(true);
    this.mensaje.set(null);
    this.currentPage.set(page);

    this.codigo = sanitizeNumbersOnly(this.codigo).slice(0, 5);
    this.descripcion = sanitizeText(this.descripcion).slice(0, 50);

    try {
      const start = page * this.pageSize();
      const [rows, total] = await Promise.all([
        this.clasificacionRepo.buscar({
          codigo: this.codigo.trim() || undefined,
          descripcion: this.descripcion.trim() || undefined,
          start,
          length: this.pageSize()
        }),
        this.clasificacionRepo.contar({
          codigo: this.codigo.trim() || undefined,
          descripcion: this.descripcion.trim() || undefined
        })
      ]);

      this.items.set(rows);
      this.totalRegistros.set(total);
      if (rows.length === 0) {
        this.mensaje.set('No se encontraron clasificaciones con los filtros ingresados.');
      }
    } catch {
      this.mensaje.set('Error al buscar clasificaciones. Intente nuevamente.');
      this.items.set([]);
      this.totalRegistros.set(0);
    } finally {
      this.loading.set(false);
    }
  }

  onPageSizeChange(): void {
    const size = Number(this.selectedPageSize);
    this.pageSize.set(size > 0 ? size : DEFAULT_ROWS_PER_PAGE);
    void this.buscar(0);
  }

  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.totalPages()) {
      return;
    }
    void this.buscar(page);
  }

  limpiarFiltros(): void {
    this.codigo = '';
    this.descripcion = '';
    this.items.set([]);
    this.totalRegistros.set(0);
    this.currentPage.set(0);
    this.mensaje.set(null);
  }

  seleccionar(item: Clasificacion): void {
    this.onSelect.emit({
      cdclasificacion: item.cdclasificacion,
      dsclasificacion: item.dsclasificacion
    });
  }

  onCancelar(): void {
    this.onClose.emit();
  }
}
